import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import EmployeeCard from '@/platform/common/components/EmployeeCard/EmployeeCard';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import { useFilters } from '@/platform/hooks/useFilters';
import { localDate } from '@/platform/lib/utils';
import {
    Button,
    Container,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { usePage } from '@inertiajs/react';
import dayjs from 'dayjs';
import SignhostFilters from './components/SignhostFilters';
import StatusLabel from './components/StatusLabel';

const ONLY = ['transactions'];

const IndexView = () => {
    const { auth, transactions, statuses } = usePage().props;
    const { updateFilters, isSorted } = useFilters(ONLY);

    const permissions = auth?.permissions || [];
    const showEntity = (auth?.customer?.entities || []).length > 1;
    const canDownload = permissions.includes('download documents');

    const columnCount = 7 + (showEntity ? 1 : 0) + (canDownload ? 1 : 0);

    return (
        <Container fluid>
            <Segment>
                <SegmentHeader
                    header={`Machtigingen (${transactions?.meta?.total || 0})`}
                    subheader='Een lijst die de statussen van machtigingen weergeeft.'
                />
                <SegmentContent>
                    <SignhostFilters statuses={statuses} />

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>
                                    Machtiging voor
                                </TableHeaderCell>
                                {showEntity && (
                                    <TableHeaderCell>Entiteit</TableHeaderCell>
                                )}
                                <TableHeaderCell>Status</TableHeaderCell>
                                <TableHeaderCell
                                    collapsing
                                    sorted={isSorted('start_date')}
                                    onClick={() =>
                                        updateFilters('sort', 'start_date')
                                    }
                                >
                                    Datum indienst
                                </TableHeaderCell>
                                <TableHeaderCell collapsing>
                                    Datum uitdienst
                                </TableHeaderCell>
                                <TableHeaderCell
                                    collapsing
                                    sorted={isSorted('created_at')}
                                    onClick={() =>
                                        updateFilters('sort', 'created_at')
                                    }
                                >
                                    Aangemaakt op
                                </TableHeaderCell>
                                <TableHeaderCell
                                    collapsing
                                    sorted={isSorted('expires_at')}
                                    onClick={() =>
                                        updateFilters('sort', 'expires_at')
                                    }
                                >
                                    Verloopt op
                                </TableHeaderCell>
                                <TableHeaderCell
                                    collapsing
                                    sorted={isSorted('signed_at')}
                                    onClick={() =>
                                        updateFilters('sort', 'signed_at')
                                    }
                                >
                                    Ondertekend op
                                </TableHeaderCell>
                                {canDownload && (
                                    <TableHeaderCell textAlign='right'>
                                        Machtiging
                                    </TableHeaderCell>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {transactions?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={columnCount} />
                            )}

                            {transactions?.data?.map(transaction => {
                                const employment =
                                    transaction?.employee
                                        ?.latest_base_employment_group;

                                return (
                                    <TableRow key={transaction.id}>
                                        <TableCell>
                                            <EmployeeCard
                                                employee={transaction?.employee}
                                                link
                                            />
                                        </TableCell>
                                        {showEntity && (
                                            <TableCell>
                                                {transaction?.entity?.name}
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <StatusLabel
                                                label={
                                                    transaction?.status_label
                                                }
                                                color={
                                                    transaction?.status_color
                                                }
                                                rejectionReason={
                                                    transaction?.rejection_reason
                                                }
                                            />
                                        </TableCell>
                                        <TableCell collapsing>
                                            {employment?.start_date
                                                ? localDate(
                                                      employment.start_date
                                                  )
                                                : ''}
                                        </TableCell>
                                        <TableCell collapsing>
                                            {employment?.end_date
                                                ? localDate(employment.end_date)
                                                : ''}
                                        </TableCell>
                                        <TableCell collapsing>
                                            {localDate(transaction?.created_at)}
                                        </TableCell>
                                        <TableCell collapsing>
                                            {localDate(transaction?.expires_at)}
                                        </TableCell>
                                        <TableCell collapsing>
                                            {transaction?.signed_at ? (
                                                dayjs(
                                                    transaction.signed_at
                                                ).format('L LT')
                                            ) : (
                                                <span className='text-gray-400'>
                                                    n.v.t.
                                                </span>
                                            )}
                                        </TableCell>
                                        {canDownload && (
                                            <TableCell textAlign='right'>
                                                {transaction?.document ? (
                                                    <Button
                                                        as='a'
                                                        variant='default'
                                                        href={`/documents/${transaction.document.id}`}
                                                        target='_blank'
                                                        rel='noreferrer'
                                                        aria-label='Machtiging downloaden'
                                                        className='bg-white px-2 shadow-[inset_0_0_0_1px_#22242626] outline-0 transition hover:bg-white hover:shadow-[inset_0_0_0_1px_#22242659]'
                                                    >
                                                        <ArrowDownTrayIcon className='size-4 text-black/60' />
                                                    </Button>
                                                ) : (
                                                    <span className='text-gray-400'>
                                                        n.v.t.
                                                    </span>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </SegmentContent>
                <SegmentFooter>
                    <CustomPagination meta={transactions?.meta} only={ONLY} />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default IndexView;
